import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AchievementBadge = ({ achievement, size = 'default', showDetails = false, onShare }) => {
  const [isHovered, setIsHovered] = useState(false);

  const getBadgeSize = () => {
    switch (size) {
      case 'small':
        return 'w-12 h-12';
      case 'large':
        return 'w-20 h-20';
      default:
        return 'w-16 h-16';
    }
  };

  const getIconSize = () => {
    switch (size) {
      case 'small':
        return 20;
      case 'large':
        return 32;
      default:
        return 24;
    }
  };

  const getBadgeColor = (category) => {
    switch (category) {
      case 'fitness':
        return 'from-success to-success/80';
      case 'nutrition':
        return 'from-accent to-accent/80';
      case 'mindfulness':
        return 'from-primary to-primary/80';
      case 'community':
        return 'from-secondary to-secondary/80';
      case 'milestone':
        return 'from-warning to-warning/80';
      default:
        return 'from-muted to-muted/80';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div 
      className={`relative group ${showDetails ? 'bg-card rounded-lg border border-border p-4 shadow-wellness' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-center space-x-4">
        <div className="relative">
          <div className={`${getBadgeSize()} rounded-full bg-gradient-to-br ${getBadgeColor(achievement?.category)} 
            flex items-center justify-center shadow-wellness-md transition-all duration-300 
            ${isHovered ? 'scale-110 shadow-wellness-lg' : ''} 
            ${achievement?.isNew ? 'animate-breathing' : ''}`}>
            <Icon 
              name={achievement?.icon} 
              size={getIconSize()} 
              color="white" 
            />
          </div>
          
          {achievement?.isNew && (
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-error rounded-full flex items-center justify-center">
              <span className="text-xs text-white font-bold">!</span>
            </div>
          )}
          
          {achievement?.level && (
            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-accent rounded-full flex items-center justify-center border-2 border-card">
              <span className="text-xs font-bold text-accent-foreground">{achievement?.level}</span>
            </div>
          )}
        </div>

        {showDetails && (
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-heading font-semibold text-foreground">{achievement?.title}</h3>
              {achievement?.points && (
                <div className="flex items-center space-x-1 bg-accent/10 px-2 py-1 rounded-full">
                  <Icon name="Star" size={14} className="text-accent" />
                  <span className="text-sm font-medium text-accent-foreground">{achievement?.points} pts</span>
                </div>
              )}
            </div>
            
            <p className="text-sm text-muted-foreground mb-3">{achievement?.description}</p>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-1">
                  <Icon name="Calendar" size={14} className="text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">
                    Earned {formatDate(achievement?.earnedDate)}
                  </span>
                </div>
                
                {achievement?.rarity && (
                  <div className="flex items-center space-x-1">
                    <Icon name="Award" size={14} className="text-primary" />
                    <span className="text-xs text-primary font-medium capitalize">{achievement?.rarity}</span>
                  </div>
                )}
              </div>
              
              {onShare && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onShare(achievement)}
                  iconName="Share2"
                  iconPosition="left"
                >
                  Share
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
      {!showDetails && isHovered && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 z-50">
          <div className="bg-popover border border-border rounded-lg p-3 shadow-wellness-lg min-w-48">
            <h4 className="font-medium text-foreground mb-1">{achievement?.title}</h4>
            <p className="text-xs text-muted-foreground mb-2">{achievement?.description}</p>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {formatDate(achievement?.earnedDate)}
              </span>
              {achievement?.points && (
                <div className="flex items-center space-x-1">
                  <Icon name="Star" size={12} className="text-accent" />
                  <span className="text-xs text-accent-foreground">{achievement?.points}</span>
                </div>
              )}
            </div>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-border"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AchievementBadge;